import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { UserCacheService } from "@/lib/user-cache";
import { appEntryFor, type AppEntry } from "@/lib/account-setup";
import { buildOnboardingUrlWithReturn } from "@/lib/auth-redirect";
import { SELLER_ROLES } from "@/lib/roles";
import { cn } from "@/lib/utils";

/**
 * A signed-in visitor's route into the app from a public share page.
 *
 * Share pages have no sidebar and no navigation, so someone signed in and looking at one has no
 * way back into ZenMusic. This is that way: one button, sitting beside the page's own primary
 * action.
 *
 * It replaced a full-width strip carrying a pitch sentence ("Find the right track. Request a
 * license…") that rotated its clauses when it did not fit. The pitch was the wrong thing to put
 * in a header — it competed with the page's actual CTA and read as marketing — so it is gone, and
 * with it the fit-measuring, rotation and reduced-motion machinery that existed only to make a
 * long sentence behave. A button always fits.
 *
 * Where it goes depends on whether the page has a header to host it (see `hostsInlineAppEntry`):
 * the two variants are mutually exclusive so no page ever offers the same link twice.
 */

export const APP_ENTRY_LABEL_ONBOARD = "Set up account";
export const APP_ENTRY_LABEL_ENTER = "Go to app";
/** A blocked account's way in is its waiting page, which says why: not "the app". */
export const APP_ENTRY_LABEL_STATUS = "Account status";

/**
 * Pure — whether to show the button at all. Fails closed: an unresolved profile (null) means
 * "don't know yet", never a guess at which label is right.
 */
export function shouldShowAppEntry({
  isAuthenticated,
  entry,
}: {
  isAuthenticated: boolean;
  entry: AppEntry | null;
}): boolean {
  if (!isAuthenticated) return false;
  return entry !== null;
}

/**
 * Pure — where the button goes and what it says. `entry` is `appEntryFor(profile)`, the rule
 * OnboardingGuard and onboarding apply, so the button never sends someone where they'd be bounced
 * straight back from.
 *
 * - Set up: straight to their own workspace.
 * - Blocked (awaiting approval, refused, suspended): the waiting page, which is their way in.
 * - Anything else: into onboarding, carrying this page so finishing returns them to it.
 */
export function resolveAppEntry({
  entry,
  role,
  returnPath,
}: {
  entry: AppEntry;
  role: string | null;
  returnPath: string;
}): { href: string; label: string } {
  if (entry === "app") {
    // Mirrors OnboardingPage's own post-onboarding destinations.
    return {
      href: role && SELLER_ROLES.has(role) ? "/tracks" : "/home",
      label: APP_ENTRY_LABEL_ENTER,
    };
  }
  if (entry === "blocked") {
    return { href: "/pending-approval", label: APP_ENTRY_LABEL_STATUS };
  }
  return {
    href: buildOnboardingUrlWithReturn(returnPath),
    label: APP_ENTRY_LABEL_ONBOARD,
  };
}

/**
 * Pure — is this pathname a public/share surface? These pages are never wrapped by `Layout`, so
 * they use `min-h-screen` rather than the app shell's hard `h-screen`.
 *
 * `/tracks` — the authenticated catalog page — is the load-bearing negative case: it must NOT
 * match the `/track/` prefix despite sharing "track". `"/tracks".startsWith("/track/")` is false
 * because the 7th character diverges (`s` vs `/`); pinned by test.
 */
const PUBLIC_SHARE_PATH_PREFIXES = [
  "/track/",
  "/playlist/share",
  "/storefront/share",
  "/shared/",
] as const;

// Deliberately NOT here: `/playlist/:id`. It is reachable unauthenticated, so it looks like a
// fifth public surface — but `ConditionalLayoutRoute` (routes.tsx:390-408) renders `<Layout>` as
// soon as `user` is truthy. The moment a visitor signs in there the page gains the full app shell
// and sidebar, so they already have a way back and need no button.
export function isPublicShareSurface(pathname: string): boolean {
  return PUBLIC_SHARE_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Surfaces whose own page header hosts the inline variant. The standalone variant stands down
 * there so the page never offers the same link twice.
 */
const INLINE_HOSTED_PREFIXES = ["/track/"] as const;

export function hostsInlineAppEntry(pathname: string): boolean {
  return INLINE_HOSTED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function AppEntryButton({
  variant = "standalone",
}: {
  /**
   * "inline" — sits in a page's existing header, styled to sit beside that page's primary CTA
   *   without competing with it (quieter: no border fill, no uppercase tracking).
   * "standalone" — for share surfaces with no header of their own; a thin right-aligned bar.
   */
  variant?: "inline" | "standalone";
} = {}) {
  const { user } = useAuth();
  const location = useLocation();
  const [entry, setEntry] = useState<AppEntry | null>(null);
  const [role, setRole] = useState<string | null>(null);

  // Keyed on the signed-in user, not on a signup event, so the button is present on EVERY visit —
  // a reload, a fresh tab, or a return days later all resolve the same way.
  useEffect(() => {
    if (!user) {
      setEntry(null);
      setRole(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const profile = await UserCacheService.getProfile(user.id);
        if (cancelled) return;
        setEntry(appEntryFor(profile));
        setRole(profile?.role ?? null);
      } catch (err) {
        if (cancelled) return;
        console.error("AppEntryButton: failed to resolve the viewer's profile", err);
        setEntry(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const supersededByHeader =
    variant === "standalone" && hostsInlineAppEntry(location.pathname);
  const visible =
    shouldShowAppEntry({ isAuthenticated: Boolean(user), entry }) &&
    isPublicShareSurface(location.pathname) &&
    !supersededByHeader;

  if (!visible || entry === null) return null;

  const { href, label } = resolveAppEntry({
    entry,
    role,
    returnPath: `${location.pathname}${location.search}`,
  });

  const button = (
    <Link
      to={href}
      className={cn(
        "group inline-flex shrink-0 items-center gap-2 rounded-none border px-4 py-2",
        "text-[0.62rem] font-semibold uppercase tracking-[0.3em] transition",
        // Quieter than the page's own primary CTA on purpose — this is a way out, not the
        // action the page is asking for.
        "border-white/15 text-white/70 hover:border-white/35 hover:text-white"
      )}
    >
      {label}
      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
    </Link>
  );

  if (variant === "inline") return button;

  return (
    <div className="flex w-full shrink-0 justify-end px-4 py-2 md:px-6">{button}</div>
  );
}
